import { useEffect, useState } from 'react';

/** Custom editor event fired whenever the host changes editability. */
export const EDITABLE_EVENT = 'quill:editable';

/**
 * Change editability without emitting a content 'update' (which would mark the document
 * dirty) and notify node views, which do not re-render on setEditable by themselves.
 * @param {import('@tiptap/core').Editor|null} editor - Editor.
 * @param {boolean} editable - Whether the editor should be editable.
 * @returns {void}
 */
export function setEditorEditable(editor, editable) {
  if (!editor || editor.isDestroyed) return;
  if (editor.isEditable !== editable) editor.setEditable(editable, false);
  editor.emit(EDITABLE_EVENT, { editable });
}

/**
 * Track whether the editor is editable (for node views).
 * @param {import('@tiptap/core').Editor|null} editor - Editor.
 * @returns {boolean} Current editability.
 */
export default function useEditorEditable(editor) {
  const [editable, setEditable] = useState(() => Boolean(editor && editor.isEditable));
  useEffect(() => {
    if (!editor) return undefined;
    const onChange = ({ editable: next }) => setEditable(Boolean(next));
    setEditable(Boolean(editor.isEditable));
    editor.on(EDITABLE_EVENT, onChange);
    return () => editor.off(EDITABLE_EVENT, onChange);
  }, [editor]);
  return editable;
}
